import { AuthTokens, KeycloakConfig } from "../types";

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_in: number;
  refresh_expires_in: number;
}

export class KeycloakTokenError extends Error {
  constructor(
    message: string,
    public readonly isSessionInvalid: boolean
  ) {
    super(message);
    this.name = "KeycloakTokenError";
  }
}

export async function refreshTokens(
  refreshToken: string,
  config: KeycloakConfig
): Promise<AuthTokens> {
  const baseUrl = config.url.replace(/\/+$/, "");
  const tokenUrl = `${baseUrl}/realms/${config.realm}/protocol/openid-connect/token`;

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    credentials: "omit",
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    const isSessionInvalid =
      error.error === "invalid_grant" && error.error_description?.includes("Session");
    throw new KeycloakTokenError(
      error.error_description || error.error || "Token-Aktualisierung fehlgeschlagen",
      isSessionInvalid
    );
  }

  const data: TokenResponse = await response.json();

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    idToken: data.id_token,
  };
}

export function isAccessTokenExpired(accessToken: string, bufferSeconds = 30): boolean {
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) {
      return true;
    }

    const payload = JSON.parse(atob(parts[1]));

    if (typeof payload.exp !== "number") {
      return true;
    }

    const expiresAt = payload.exp * 1000;
    return Date.now() >= expiresAt - bufferSeconds * 1000;
  } catch {
    return true;
  }
}

export function isRefreshTokenExpired(refreshToken: string): boolean {
  try {
    const parts = refreshToken.split(".");
    if (parts.length !== 3) {
      return true;
    }

    const payload = JSON.parse(atob(parts[1]));

    if (typeof payload.exp !== "number") {
      return true;
    }

    const expiresAt = payload.exp * 1000;
    return Date.now() >= expiresAt;
  } catch {
    return true;
  }
}
